import { Component, inject, Inject, OnInit, Optional, Type, ViewEncapsulation, OnDestroy } from '@angular/core';
import { RouterEventsService } from '../../../services/router-events/router-events.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { GungLanguageConfigService } from 'gung-common';
import { GungListRouterEventsService } from 'gung-list';
import { TranslateService } from '@ngx-translate/core';
import { SelectedCustomerService } from '../../../services/selected-customer/selected-customer.service';
import { AuthService } from '../../../services/auth/auth.service';
import { NavigationConfig } from '../../../models/navigation-config';
import { filter, first, takeUntil, Subject } from 'rxjs';
import { MetadataService } from '../../../services/metadata/metadata.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state/state.module';
import { LoadCart } from '../../../state/cart/actions';
import { BaseViewConfigService } from '../../../services/base-view-config/base-view-config.service';
import { FooterComponent } from '../../footer/footer.component';
import { GungVersionValidatorService } from '../../../services/gung-version-validator.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { SetCustomerFromQueryParam } from '../../../state/customer/actions';
import { GungCompanyService } from '../../../services/gung-company/gung-company.service';
import { PageContainerService } from '../../../services/page-container/page-container.service';

export enum GungLoadingState {
  LOADING,
  VALID,
  INVALID
}

@Component({
    selector: 'lib-gung-app',
    templateUrl: './gung-app.component.html',
    styleUrls: ['./gung-app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GungAppComponent implements OnInit, OnDestroy {
  protected router = inject(Router);
  protected store = inject<Store<AppState>>(Store<AppState>);
  protected route = inject(ActivatedRoute);
  protected routerEventsService = inject(RouterEventsService);
  protected gungLanguageConfigService = inject(GungLanguageConfigService);
  protected gungListRouterEventsService = inject(GungListRouterEventsService);
  protected translate = inject(TranslateService);
  protected authService = inject(AuthService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected metadataService = inject(MetadataService);
  protected baseViewConfigServicei = inject(BaseViewConfigService);
  protected gungVersionValidatorService = inject(GungVersionValidatorService);
  protected modalService = inject(GungModalService);
  // The line below is needed because of the tree shaking. If the service is not used in the component,
  // it will be removed from the dependency tree. This ensures that the service is not removed. - Adam
  protected googleAnalyticsService = inject(GoogleAnalyticsService);
  protected gungCompanyService = inject(GungCompanyService);
  protected pageContainerService = inject(PageContainerService);

  cookiesEnabled: boolean = this.environment.enableCookies;
  unsubscribe: Subject<boolean> = new Subject<boolean>();
  isLoading = true;
  errorOccured = false;
  loginRequired = true;
  protected states: { [identifier: string]: GungLoadingState } = {};
  public footerComponent: Type<FooterComponent>;

  protected pageContainerMaxWidth: number | null = null;
  protected pageContainerExcludedUrls: string[] = [];
  mainStyle: { [key: string]: string } = { 'width': '100%' };

  protected updateMainStyle(url: string): void {
    const base = { 'width': '100%' };
    if (!this.pageContainerMaxWidth) {
      this.mainStyle = base;
      return;
    }
    const allExcludedUrls = [...this.pageContainerExcludedUrls, ...this.pageContainerService.fullscreenUrls];
    const isExcluded = allExcludedUrls.some(excludedUrl => url.includes(excludedUrl));
    this.mainStyle = isExcluded
      ? base
      : { ...base, 'max-width': `${this.pageContainerMaxWidth}px`, 'margin-left': 'auto', 'margin-right': 'auto' };
  }

  cartListener = (e: StorageEvent) => {
    if (e.key.startsWith('cart_') && e.oldValue !== e.newValue) {
      this.store.dispatch(new LoadCart({ cartKey: e.key }));
    }
  };

  constructor(
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    this.routerEventsService.setRouter(this.router);
    this.gungListRouterEventsService.setRouter(this.router);
    this.gungLanguageConfigService.selectLanguage();

    this.route.queryParams.subscribe(value => {
      let language = value['language'];
      if (!!language) this.translate.use(language);

      const selectCustomer = value['setSelectedCustomer'];

      //we want set selected customer from queryparams
      if (selectCustomer) {
       this.store.dispatch(new SetCustomerFromQueryParam({ customerId: selectCustomer }));
      }
    });

    this.footerComponent = this.baseViewConfigServicei.getFooterComponent();

    if (environment.robotsMetaHeader) {
      const metaTag = document.createElement('meta');
      metaTag.name = 'robots';
      metaTag.content = environment.robotsMetaHeader;
      document.getElementsByTagName('head')[0].appendChild(metaTag);
    }
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.unsubscribe)
    ).subscribe((event: NavigationEnd) => {
      this.updateMainStyle(event.urlAfterRedirects);
    });

    this.gungCompanyService.getPublicGungCompany().pipe(first()).subscribe(company => {
      const layout = company?.extra?.layout;
      if (layout) {
        this.pageContainerMaxWidth = layout.maxWidth || null;
        this.pageContainerExcludedUrls = layout.excludedUrls || [];
      }
      this.updateMainStyle(this.router.url);
    });

    this.authService.getIsAuthenticated().subscribe(authenticated => {
      this.loginRequired = !authenticated;

      if (!!authenticated) {
        this.subscribeToState();
        window.addEventListener('storage', this.cartListener, false);
      }

      this.updateAppState();
    });

    if (this.environment.disableVersionCheck !== true) {
      this.gungVersionValidatorService.newVersionAvailableObservable
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(newVersionAvailable => {
          if (newVersionAvailable) {
            this.modalService.openNewVersionAvailableModal();
          }
        });
    }
  }

  protected subscribeToState() {
    this.subscribeCustomer();
    this.subscribeMetadata();
    this.subscribeFlow();
  }

  protected subscribeMetadata() {
    this.states.metadataLoaded = GungLoadingState.LOADING;
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe({
        next: roles => {
          if (roles.indexOf('ANONYMOUS') >= 0) {
            this.states.metadataLoaded = GungLoadingState.VALID;
            this.updateAppState();
          } else {
            this.metadataService.getMetadataCompleted().subscribe(_ => {
              this.states.metadataLoaded = GungLoadingState.VALID;
              this.updateAppState();
            });
          }
        },
        error: _ => {
          this.states.metadataLoaded = GungLoadingState.INVALID;
          this.updateAppState();
        }
      });
  }

  protected subscribeCustomer() {
    this.states.selectedCustomer = GungLoadingState.LOADING;
    this.store
      .select(state => state.customer)
      .subscribe({
        next: customerState => {
          if (customerState.doneLoading) {
            if (!!customerState.selectedCustomer) {
              this.states.selectedCustomer = GungLoadingState.VALID;
            } else {
              this.states.selectedCustomer = GungLoadingState.INVALID;
            }
          }

          this.updateAppState();
        },
        error: _ => {
          this.states.selectedCustomer = GungLoadingState.INVALID;
          this.updateAppState();
        }
      });
  }

  protected subscribeFlow() {
    this.states.selectedFlow = GungLoadingState.LOADING;
    this.store
      .select(state => state.gungFlow)
      .subscribe({
        next: gungFlowState => {
          if (gungFlowState.doneLoading) {
            if (!!gungFlowState.selectedFlow) {
              this.states.selectedFlow = GungLoadingState.VALID;
            } else {
              this.states.selectedFlow = GungLoadingState.INVALID;
            }
          }

          this.updateAppState();
        },
        error: _ => {
          this.states.selectedFlow = GungLoadingState.INVALID;
          this.updateAppState();
        }
      });
  }

  updateAppState() {
    const keys = Object.keys(this.states);

    const filteredKeys = keys.filter(key => {
      return this.states[key] === GungLoadingState.LOADING;
    });

    this.isLoading = filteredKeys.length !== 0;

    const invalidKeys = keys.filter(key => {
      return this.states[key] === GungLoadingState.INVALID;
    });
    const error = invalidKeys.length > 0;

    if (error) {
      console.error(`Error in the following keys: ${invalidKeys}`);
    }
  }

  logout() {
    this.authService.logout();
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
    window.removeEventListener('storage', this.cartListener, false);
  }
}
